import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  IndianRupee, 
  Plus, 
  Loader2,
  BarChart3,
  Info,
  ExternalLink
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export const FundDetailPage = () => {
  const { schemeCode } = useParams();
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFundDetails();
  }, [schemeCode]);

  const fetchFundDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/funds/${schemeCode}`);
      setFund(response.data);
    } catch (error) {
      console.error("Error fetching fund details:", error);
      setError("Failed to load fund details. Please try again.");
      // Set demo data for testing
      setFund({
        meta: {
          scheme_code: schemeCode,
          scheme_name: "Sample Mutual Fund",
          scheme_type: "Open Ended",
          scheme_category: "Equity",
          fund_house: "Sample AMC"
        },
        data: [
          { date: "01-01-2024", nav: "100.00" },
          { date: "01-02-2024", nav: "102.50" },
          { date: "01-03-2024", nav: "101.20" },
          { date: "01-04-2024", nav: "105.80" },
          { date: "01-05-2024", nav: "108.30" },
          { date: "01-06-2024", nav: "112.50" },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const addToPortfolio = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to add funds to portfolio");
      return;
    }
    
    try {
      await axios.post(`${API}/portfolio`, {
        scheme_code: schemeCode,
        scheme_name: fund?.meta?.scheme_name || "Unknown Fund",
        units: 100,
        purchase_nav: parseFloat(fund?.data?.[0]?.nav || 100),
        purchase_date: new Date().toISOString().split('T')[0]
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Fund added to portfolio!");
    } catch (error) {
      toast.error("Failed to add fund to portfolio");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  // Prepare chart data
  const chartData = fund?.data?.slice(0, 30).reverse().map(item => ({
    date: item.date,
    nav: parseFloat(item.nav)
  })) || [];

  // Calculate returns
  const latestNav = parseFloat(fund?.data?.[0]?.nav || 0);
  const oldNav = parseFloat(fund?.data?.[Math.min(fund?.data?.length - 1, 365)]?.nav || latestNav);
  const returns1Y = oldNav > 0 ? (((latestNav - oldNav) / oldNav) * 100).toFixed(2) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-4">Loading fund details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="fund-detail-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Link to="/research" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Research
          </Link>

          {error && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
              <p className="text-yellow-500">{error}</p>
            </div>
          )}

          {/* Fund Header */}
          <Card className="glass-card mb-8">
            <CardContent className="p-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold text-xl">
                        {fund?.meta?.fund_house?.charAt(0) || "F"}
                      </span>
                    </div>
                    <div>
                      <h1 className="font-serif text-2xl font-bold" data-testid="fund-name">
                        {fund?.meta?.scheme_name}
                      </h1>
                      <p className="text-muted-foreground">{fund?.meta?.fund_house}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{fund?.meta?.scheme_category}</Badge>
                    <Badge variant="outline" className="border-white/10">{fund?.meta?.scheme_type}</Badge>
                    <Badge variant="outline" className="border-white/10">Code: {schemeCode}</Badge>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-4">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Current NAV</p>
                    <p className="font-mono text-3xl font-bold text-primary" data-testid="current-nav">
                      {formatCurrency(latestNav)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      as of {fund?.data?.[0]?.date || "N/A"}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button variant="outline" className="border-white/10" asChild>
                      <Link to={`/compare?funds=${schemeCode}`}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Compare
                      </Link>
                    </Button>
                    <Button className="btn-primary" onClick={addToPortfolio} data-testid="add-to-portfolio-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add to Portfolio
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">1Y Returns</p>
                <div className={`flex items-center justify-center gap-1 font-mono text-2xl font-bold ${
                  parseFloat(returns1Y) >= 0 ? "text-green-500" : "text-red-500"
                }`}>
                  {parseFloat(returns1Y) >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  {parseFloat(returns1Y) >= 0 ? "+" : ""}{returns1Y}%
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">Min SIP</p>
                <p className="font-mono text-2xl font-bold">₹500</p>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">Min Lumpsum</p>
                <p className="font-mono text-2xl font-bold">₹5,000</p>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">Expense Ratio</p>
                <p className="font-mono text-2xl font-bold">1.25%</p>
              </CardContent>
            </Card>
          </div>

          {/* NAV Chart */}
          <Card className="glass-card mb-8">
            <CardHeader>
              <CardTitle className="font-serif text-xl">NAV History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorNav" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value?.split('-').slice(0, 2).join('/')}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      tick={{ fontSize: 12 }}
                      domain={['dataMin - 5', 'dataMax + 5']}
                      tickFormatter={(v) => `₹${v}`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), 'NAV']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="nav" 
                      stroke="hsl(var(--chart-1))" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorNav)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Fund Information Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-card border border-border">
              <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
              <TabsTrigger value="returns" data-testid="returns-tab">Returns</TabsTrigger>
              <TabsTrigger value="holdings" data-testid="holdings-tab">Holdings</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <Card className="glass-card">
                <CardContent className="p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Info className="w-4 h-4 text-primary" />
                        Fund Details
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Fund House</span>
                          <span>{fund?.meta?.fund_house}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Category</span>
                          <span>{fund?.meta?.scheme_category}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Type</span>
                          <span>{fund?.meta?.scheme_type}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Scheme Code</span>
                          <span className="font-mono">{schemeCode}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold">Investment Details</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Min SIP</span>
                          <span>₹500</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Min Lumpsum</span>
                          <span>₹5,000</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Exit Load</span>
                          <span>1% if redeemed within 1 year</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Lock-in Period</span>
                          <span>None</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="returns">
              <Card className="glass-card">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Period</th>
                          <th className="text-right py-3 px-4 text-muted-foreground font-medium">Returns</th>
                          <th className="text-right py-3 px-4 text-muted-foreground font-medium">Benchmark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { period: "1 Month", returns: "2.5%", benchmark: "2.1%" },
                          { period: "3 Months", returns: "8.2%", benchmark: "7.5%" },
                          { period: "6 Months", returns: "14.5%", benchmark: "12.8%" },
                          { period: "1 Year", returns: `${returns1Y}%`, benchmark: "18.5%" },
                          { period: "3 Years", returns: "18.2%", benchmark: "16.5%" },
                          { period: "5 Years", returns: "15.8%", benchmark: "14.2%" },
                        ].map((row, index) => (
                          <tr key={index} className="border-b border-border">
                            <td className="py-3 px-4">{row.period}</td>
                            <td className="py-3 px-4 text-right font-mono text-green-500">{row.returns}</td>
                            <td className="py-3 px-4 text-right font-mono text-muted-foreground">{row.benchmark}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="holdings">
              <Card className="glass-card">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Top 10 Holdings</h3>
                  <div className="space-y-3">
                    {[
                      { name: "HDFC Bank Ltd", weight: "9.5%" },
                      { name: "ICICI Bank Ltd", weight: "8.2%" },
                      { name: "Reliance Industries Ltd", weight: "7.8%" },
                      { name: "Infosys Ltd", weight: "6.5%" },
                      { name: "TCS Ltd", weight: "5.8%" },
                      { name: "Bharti Airtel Ltd", weight: "4.2%" },
                      { name: "Kotak Mahindra Bank", weight: "3.9%" },
                      { name: "Axis Bank Ltd", weight: "3.5%" },
                      { name: "Larsen & Toubro Ltd", weight: "3.2%" },
                      { name: "State Bank of India", weight: "2.8%" },
                    ].map((holding, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-border">
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground w-6">{index + 1}.</span>
                          <span>{holding.name}</span>
                        </div>
                        <span className="font-mono">{holding.weight}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};
